import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from 'axios';
import { 
  GET_ENTITY_TYPE_ATTRIBUTE_URL,
  get_headers,
  get_project_id,
  get_token,
  SUBMIT_ATTRIBUTE_VALUES_URL,
} from '@/utils/constants/constants.js';

export default {
  name: "ApplicationAttributes",
  mixins: [alert_mixin],
  data: () => ({
    
      entity_profile_id: sessionStorage.getItem("entity_profile_id"),
      headers: get_headers(),
      token: get_token(),
      project_id: get_project_id(),
      attributes: [],
      user_role: '', // You can set this based on your auth system
      current_attribute: null,
      request_info_note: '',
      empty_message: "",
  }),
  mounted() {
    this.get_attributes_list();
  },

  methods: {
    async get_attributes_list() {
      this.is_loading = true;
      this.empty_message = "";

      try {
      const response = await this.fetch_attributes();
      const raw_attributes = this.extract_raw_attributes(response?.data);

      if (!raw_attributes.length) {
        this.attributes = [];
        this.empty_message = "No attributes are assigned to this profile yet.";
        return;
      }

      this.attributes = raw_attributes.map((rec) => this.normalize_attribute_record(rec));
      } catch (error) {
      this.attributes = [];
      this.empty_message = "Could not load attributes.";
      this.show_error(error?.response?.data?.message || error.message || this.empty_message);
      } finally {
      this.is_loading = false;
      }
    },

    fetch_attributes() {
      const params = { entity_profile_id: this.entity_profile_id };
      return axios.get(GET_ENTITY_TYPE_ATTRIBUTE_URL, {
      headers: this.headers,
      params,
      });
    },

    extract_raw_attributes(api_data) {
      if (!api_data) return [];
      if (Array.isArray(api_data.data)) return api_data.data;
      return [];
    },

    to_array(raw_value) {
      if (this.is_array(raw_value)) return raw_value;
      if (!this.is_string(raw_value)) return [];

      const trimmed = raw_value.trim();
      if (!trimmed) return [];

      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      return this.parse_bracketed(trimmed);
      }

      if (trimmed.includes(",")) {
      return this.split_inner_by_comma(trimmed);
      }

      return [trimmed];
    },

    is_array(value) {
      return Array.isArray(value);
    },

    is_string(value) {
      return typeof value === "string";
    },

    try_parse_json(input) {
      const parsed = JSON.parse(input);
      return Array.isArray(parsed) ? parsed : null;
    },

    strip_quotes_and_spaces(str) {
      return String(str).replace(/^['"\s]+|['"\s]+$/g, "");
    },

    split_inner_by_comma(input) {
      return String(input)
      .split(",")
      .map((piece) => this.strip_quotes_and_spaces(piece))
      .filter(Boolean);
    },

    parse_bracketed(input) {
      const parsed = this.try_parse_json(input);
      if (parsed) return parsed;

      const parsed_single_quotes = this.try_parse_json(input.replace(/'/g, '"'));
      if (parsed_single_quotes) return parsed_single_quotes;

      return this.split_inner_by_comma(input.slice(1, -1));
    },

    normalize_options(choices) {
      return (choices || []).map((choice_item) =>
      typeof choice_item === "string"
        ? { text: choice_item, value: choice_item }
        : {
          text:
          choice_item.choice ??
          choice_item.text ??
          choice_item.value ??
          "",
          value:
          choice_item.value ??
          choice_item.choice ??
          choice_item.text ??
          "",
        }
      );
    },

    normalize_attribute_record(attribute_record) {
      const question_obj = attribute_record.question || {};
      const question_type = String(question_obj.question_type || "short_text").toLowerCase();

      const normalized_options = this.normalize_options(question_obj.choices || question_obj.options || []);

      const normalized_answer =
      question_type === "multi_select"
        ? this.to_array(attribute_record.answer_value)
        : attribute_record.answer_value;

      const result = {
        id: attribute_record.id,
        question_text: question_obj.question_text || "No question",
        question_type,
        question: question_obj,
        options: normalized_options,
        answer_value: normalized_answer,
        files: attribute_record.files || [],
        is_verified: !!attribute_record.is_verified,
        date_created: attribute_record.date_created,
        date_updated: attribute_record.date_updated,
        required: !!question_obj.is_required,
        min: question_obj.min_value ?? question_obj.min ?? null,
        max: question_obj.max_value ?? question_obj.max ?? null,
        step: question_obj.step ?? null,
      };

      return result;
    },

    update_answer(attribute_id, value) {
      const attribute = this.attributes.find(a => a.id === attribute_id);
      if (attribute) {
        attribute.answer_value = value;
        // You can add API call here to save the answer
      }
    },

    get_file_name(file) {
      return file.name || 'Uploaded file';
    },

    get_file_url(file) {
      return file.presigned_url || file.original_url || '#';
    },

    remove_file(attribute_id) {
      const attribute = this.attributes.find(a => a.id === attribute_id);
      if (attribute) {
        attribute.files = [];
        // You can add API call here to remove the file
      }
    },

    verify_attribute(attribute_id) {
      // Implement verification logic
      const attribute = this.attributes.find(a => a.id === attribute_id);
      if (attribute) {
        attribute.is_verified = true;
        this.show_success("Attribute verified successfully!");
      }
    },

    set_current_attribute(attribute) {
      this.current_attribute = attribute;
    },

    request_information() {
      // Implement request information logic
      this.show_success("Information request sent!");
      this.request_info_note = '';
    },

    admin_action(action) {
      // Implement approve/reject logic
      this.show_success(`All attributes ${action}ed successfully!`);
    },

    async submit_values() {
      const entity_id = this.parse_entity_id(this.entity_profile_id);
      if (!entity_id) return;

      const attribute_values = this.build_attribute_values(this.attributes || []);

      this.is_submitting = true;
      try {
      const response = await this.post_attribute_values(entity_id, attribute_values);

      if (response?.data?.status === "success") {
        this.show_success("Values submitted successfully");
      } else {
        this.show_error(response?.data?.message || "Could not save attributes");
      }
      } catch (error) {
      this.show_error(error?.response?.data?.message || error.message || "Save failed");
      } finally {
      this.is_submitting = false;
      }
    },

    parse_entity_id(rawId) {
      const entity_id = Number(rawId);
      if (!Number.isFinite(entity_id) || entity_id <= 0) {
      this.show_error("Invalid entity_profile_id");
      return null;
      }
      return entity_id;
    },

    is_array_value(v) {
      return Array.isArray(v);
    },

    is_string_value(v) {
      return typeof v === "string";
    },

    trim_value(s) {
      return String(s).trim();
    },

    is_bracketed_value(s) {
      return this.is_string_value(s) && s.startsWith("[") && s.endsWith("]");
    },

    parse_json_safe(s) {
      if (!this.is_string_value(s)) return null;
      try {
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) ? parsed : null;
      } catch {
      return null;
      }
    },

    split_and_clean_by_comma(s) {
      return String(s)
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    },

    convert_to_array(input_value) {
      if (this.is_array_value(input_value)) return input_value;
      if (!this.is_string_value(input_value)) return [];

      const trimmed_value = this.trim_value(input_value);
      if (!trimmed_value) return [];

      if (this.is_bracketed_value(trimmed_value)) {
      const parsed = this.parse_json_safe(trimmed_value) || this.parse_json_safe(trimmed_value.replace(/'/g, '"'));
      if (parsed) return parsed;
      return this.split_and_clean_by_comma(trimmed_value.slice(1, -1));
      }

      if (trimmed_value.includes(",")) {
      return this.split_and_clean_by_comma(trimmed_value);
      }

      return [trimmed_value];
    },

    normalize_attribute_value(attribute_item) {
      const attribute_type = String(attribute_item.question_type || "").toLowerCase();
      if (attribute_type === "multi_select") {
      return this.convert_to_array(attribute_item.answer_value);
      }
      if (attribute_type === "file") {
      return (attribute_item.files || []).map((file) => file.uuid || file.id || file.name).filter(Boolean);
      }
      if (attribute_type === "number") {
      const numeric_value = Number(attribute_item.answer_value);
      return Number.isFinite(numeric_value) ? numeric_value : null;
      }
      if (attribute_type === 'date') {
      return attribute_item.answer_value ? String(attribute_item.answer_value) : '';
      }
      return typeof attribute_item.answer_value === "string"
      ? attribute_item.answer_value
      : String(attribute_item.answer_value ?? "");
    },

    build_attribute_values(attributes) {
      return attributes.map((attribute_item) => ({
      entity_type_attribute_id: Number(attribute_item.id),
      values: this.normalize_attribute_value(attribute_item),
      }));
    },

    post_attribute_values(entity_id, attribute_values) {
      return axios.post(
      SUBMIT_ATTRIBUTE_VALUES_URL,
      { entity_profile_id: Number(entity_id), attribute_values },
      { headers: this.headers }
      );
    },

  }
};